import React, { useEffect, useState } from 'react';

import { Layout } from 'ds';

import { AIAssistantMessage } from './types';

interface Props {
  message: AIAssistantMessage;
  onComplete?: () => void;
}

// Configurable chunk size for rendering text
const CHUNK_SIZE = 15; // Number of characters to render at a time

const AIAssistantTypingMessage: React.FC<Props> = ({ message, onComplete }) => {
  const [visibleText, setVisibleText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  // Function to find the next safe position to cut the HTML content
  const findNextSafePosition = (content: string, startIndex: number, chunkSize: number): number => {
    const endIndex = Math.min(startIndex + chunkSize, content.length);

    // If we're at the end, return the end
    if (endIndex >= content.length) {
      return content.length;
    }

    let position = endIndex;

    // Check if we're in the middle of an opening tag
    const beforePosition = content.substring(0, position);
    const lastOpenTagIndex = beforePosition.lastIndexOf('<');
    const lastCloseTagIndex = beforePosition.lastIndexOf('>');

    // If we have an unclosed opening tag, find the closing bracket
    if (lastOpenTagIndex > lastCloseTagIndex) {
      const closingBracketIndex = content.indexOf('>', lastOpenTagIndex);
      if (closingBracketIndex !== -1) {
        position = closingBracketIndex + 1;
      }
    }

    // Check if we're in the middle of a closing tag
    const afterCurrentPosition = content.substring(position);
    const nextOpenTagIndex = afterCurrentPosition.indexOf('<');
    const nextCloseTagIndex = afterCurrentPosition.indexOf('>');

    // If we have an incomplete closing tag, find the closing bracket
    if (nextOpenTagIndex !== -1 && nextCloseTagIndex !== -1 && nextOpenTagIndex < nextCloseTagIndex) {
      const tagName = afterCurrentPosition.substring(nextOpenTagIndex + 1, nextCloseTagIndex);
      if (tagName.startsWith('/')) {
        // This is a closing tag, make sure we include it completely
        position = position + nextCloseTagIndex + 1;
      }
    }

    return position;
  };

  useEffect(() => {
    // Reset state when message changes
    setVisibleText('');
    setCurrentIndex(0);
  }, [message.id]);

  useEffect(() => {
    if (currentIndex >= message.content.length) {
      onComplete?.();
      return;
    }

    const timer = setTimeout(() => {
      const nextSafePosition = findNextSafePosition(message.content, currentIndex, CHUNK_SIZE);
      const nextChunk = message.content.slice(currentIndex, nextSafePosition);
      setVisibleText(prev => prev + nextChunk);
      setCurrentIndex(nextSafePosition);
    }, 100); // Slightly faster for smoother effect

    return () => clearTimeout(timer);
  }, [currentIndex, message.content, onComplete]); // eslint-disable-line react-hooks/exhaustive-deps

  const style = {
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0.32px',
    width: '100%'
  };

  return (
    <Layout position="relative">
      <div
        style={{
          ...style,
          position: 'absolute',
          top: 0,
          left: 0,
          opacity: 0,
          animation: 'fadeIn 0.3s ease-out forwards'
        }}
        dangerouslySetInnerHTML={{ __html: visibleText }}
      />
      <div
        style={{
          ...style,
          visibility: 'hidden'
        }}
        dangerouslySetInnerHTML={{ __html: message.content }}
      />
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(2px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
    </Layout>
  );
};

export default AIAssistantTypingMessage;
